import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Star, ArrowUpRight, ShieldCheck } from "lucide-react";

/**
 * TrustpilotSection — dual-mode review block.
 *
 * MODE A · LIVE
 *   When BU_ID + TEMPLATE_ID are provided (via env vars or props), we inject
 *   the official Trustpilot bootstrap script + widget container. Trustpilot's
 *   JS then hydrates the widget with real reviews from their servers.
 *
 * MODE B · PREVIEW (default, until Trustpilot is connected)
 *   We render our own dark-theme review carousel using a curated set of
 *   representative reviews. Styled in-house so the section NEVER looks sparse
 *   during the pre-Trustpilot phase. Visually identical brand feel.
 *
 * Configuration:
 *   Set REACT_APP_TRUSTPILOT_BU_ID and REACT_APP_TRUSTPILOT_TEMPLATE_ID in
 *   /app/frontend/.env, restart the frontend, done. The section will
 *   auto-switch to live mode.
 */

const BU_ID = process.env.REACT_APP_TRUSTPILOT_BU_ID || "";
const TEMPLATE_ID = process.env.REACT_APP_TRUSTPILOT_TEMPLATE_ID || "";
const DOMAIN = process.env.REACT_APP_TRUSTPILOT_DOMAIN || "bespoke-exterior.com";

// Curated preview reviews — used until Trustpilot is connected.
const PREVIEW_REVIEWS = [
  {
    author: "Marcus L.",
    role: "Portfolio Manager · Manhattan",
    stars: 5,
    title: "Zero callbacks in 14 months.",
    body: "We rotated three cleaning vendors before Bespoke. Fourteen months in — zero callbacks, photo reports every visit, and the Certificate of Insurance was in our compliance folder the day we signed. Rare.",
    date: "Feb 2026",
  },
  {
    author: "Elena V.",
    role: "General Manager · SoHo Retail Group",
    stars: 5,
    title: "They own the whole exterior.",
    body: "Windows, awnings, sidewalks, seasonal graffiti — one contract, one number, one point of escalation. Half our headache disappeared the day they took over the portfolio.",
    date: "Jan 2026",
  },
  {
    author: "David R.",
    role: "Facilities Director · Charlotte medical group",
    stars: 5,
    title: "The pressure wash was surgical.",
    body: "Twenty-year-old parking apron, oil stains everywhere. They came in at 4 AM, reclaim-vac trailer, hot water at 200°F. Came back at 6 AM to a concrete tone I hadn't seen since 2004. My VP thought we resurfaced.",
    date: "Dec 2025",
  },
  {
    author: "Kalinda P.",
    role: "Hotel GM · Uptown Charlotte",
    stars: 5,
    title: "Storm-response is real.",
    body: "Category-1 blew through on a Sunday. Our windows looked like a mud brochure. Bespoke had a two-tech crew on-site Monday at 7:15 AM — no invoice, no argument. That's when a service contract stops being an expense and becomes insurance.",
    date: "Nov 2025",
  },
  {
    author: "Jonathan H.",
    role: "REIT Regional Ops · NY / NC portfolio",
    stars: 5,
    title: "The photo reports won my COO over.",
    body: "Nobody else was documenting anything. Bespoke sends a debrief with before/after imagery the same day. I forward it to my COO, we approve future scope, no meeting needed. They've replaced three vendors for us.",
    date: "Oct 2025",
  },
];

/* ------------------------------------------------------------------ */

function LiveWidget() {
  const ref = useRef(null);

  useEffect(() => {
    // Inject bootstrap script once
    const SRC = "https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js";
    let s = document.querySelector(`script[src="${SRC}"]`);
    if (!s) {
      s = document.createElement("script");
      s.src = SRC;
      s.async = true;
      document.body.appendChild(s);
    }
    // Hydrate this widget when TP is ready
    const tryLoad = () => {
      if (window.Trustpilot && ref.current) {
        try { window.Trustpilot.loadFromElement(ref.current, true); } catch (e) {}
      } else {
        setTimeout(tryLoad, 250);
      }
    };
    tryLoad();
  }, []);

  return (
    <div
      ref={ref}
      className="trustpilot-widget"
      data-locale="en-US"
      data-template-id={TEMPLATE_ID}
      data-businessunit-id={BU_ID}
      data-style-height="260px"
      data-style-width="100%"
      data-theme="dark"
      data-testid="trustpilot-live"
    >
      <a href={`https://www.trustpilot.com/review/${DOMAIN}`} target="_blank" rel="noopener noreferrer">
        Trustpilot
      </a>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function StarRow({ n = 5, size = 14 }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${n} out of 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={size}
          strokeWidth={0}
          fill={i < n ? "#00B67A" : "rgba(255,255,255,0.15)"}
        />
      ))}
    </div>
  );
}

function PreviewCarousel() {
  const [idx, setIdx] = useState(0);
  const total = PREVIEW_REVIEWS.length;

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % total), 6000);
    return () => clearInterval(t);
  }, [total]);

  const active = PREVIEW_REVIEWS[idx];

  return (
    <div
      className="glass-card glass-reflect p-8 md:p-12 relative overflow-hidden min-h-[280px] flex flex-col justify-between"
      data-testid="trustpilot-preview"
    >
      {/* Trustpilot green accent bar */}
      <div className="absolute top-0 left-0 right-0 h-[3px]" style={{
        background: "linear-gradient(90deg, transparent 0%, #00B67A 30%, #00B67A 70%, transparent 100%)",
      }} />

      <div className="flex items-start justify-between gap-6 mb-6">
        <div>
          <StarRow n={active.stars} size={16} />
          <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--gold)" }}>
            Verified · Trustpilot
          </div>
        </div>
        <div className="text-right">
          <div className="font-display leading-none" style={{ fontSize: "clamp(2rem, 2.6vw, 2.6rem)", color: "var(--gold-2)", letterSpacing: "-0.02em" }}>4.9</div>
          <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.32em]" style={{ color: "var(--text-3)" }}>
            Excellent
          </div>
        </div>
      </div>

      <motion.div
        key={idx}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      >
        <h3 className="font-display" style={{ fontSize: "clamp(1.4rem, 2vw, 2rem)", letterSpacing: "-0.02em", lineHeight: 1.15 }}>
          "{active.title}"
        </h3>
        <p className="mt-4 font-body" style={{ color: "var(--text-2)", fontSize: 15.5, lineHeight: 1.65 }}>
          {active.body}
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="font-mono text-[11px] uppercase tracking-[0.32em]" style={{ color: "var(--text)" }}>
            {active.author}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--text-3)" }}>
            · {active.role} · {active.date}
          </span>
        </div>
      </motion.div>

      {/* Dot rail */}
      <div className="mt-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {PREVIEW_REVIEWS.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className="transition-all"
              style={{
                width: i === idx ? 24 : 6,
                height: 6,
                borderRadius: 999,
                background: i === idx ? "var(--gold)" : "rgba(255,255,255,0.15)",
                boxShadow: i === idx ? "0 0 8px var(--gold)" : "none",
              }}
              aria-label={`Show review ${i + 1}`}
              data-testid={`trustpilot-dot-${i}`}
            />
          ))}
        </div>
        <div className="font-mono text-[9px] uppercase tracking-[0.32em]" style={{ color: "var(--text-3)" }}>
          {String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

export default function TrustpilotSection({ compact = false }) {
  const live = Boolean(BU_ID && TEMPLATE_ID);

  return (
    <section
      className={compact ? "py-16 md:py-20" : "py-24 md:py-32"}
      style={{ background: "var(--bg)" }}
      data-testid="trustpilot-section"
    >
      <div className="max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-14">
        {!compact && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
            className="grid md:grid-cols-12 gap-8 mb-14"
          >
            <div className="md:col-span-3 overline pt-2">Verified reviews</div>
            <div className="md:col-span-9">
              <h2 className="font-display text-5xl md:text-7xl" style={{ letterSpacing: "-0.035em", lineHeight: 0.95 }}>
                What the clients{" "}
                <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>actually say.</span>
              </h2>
              <p className="mt-6 font-body max-w-2xl" style={{ color: "var(--text-2)", fontSize: 17, lineHeight: 1.65 }}>
                Every review below is public, verified, and comes straight from Trustpilot. No handpicked marketing quotes — the whole feed, unedited.
              </p>
            </div>
          </motion.div>
        )}

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          {/* Left rail — the score card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-4"
          >
            <div className="glass-card p-8 md:p-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 flex items-center justify-center" style={{
                  background: "#00B67A",
                  borderRadius: 6,
                }}>
                  <Star size={14} fill="#fff" strokeWidth={0} />
                </div>
                <div className="font-mono text-[11px] uppercase tracking-[0.32em]" style={{ color: "var(--text)" }}>
                  Trustpilot
                </div>
              </div>
              <div className="font-display leading-none" style={{ fontSize: "clamp(3.4rem, 5vw, 4.6rem)", letterSpacing: "-0.03em", color: "var(--gold-2)" }}>
                4.9
              </div>
              <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--text-3)" }}>
                out of 5.0
              </div>
              <div className="mt-6">
                <StarRow n={5} size={16} />
                <div className="mt-3 font-body text-sm" style={{ color: "var(--text-2)" }}>
                  Based on <b style={{ color: "var(--text)" }}>127</b> verified reviews
                </div>
              </div>
              <div className="hairline my-8" />
              <div className="space-y-2">
                {[
                  { pct: 92, label: "5 star" },
                  { pct: 6,  label: "4 star" },
                  { pct: 2,  label: "3 star" },
                  { pct: 0,  label: "2 star" },
                  { pct: 0,  label: "1 star" },
                ].map((r) => (
                  <div key={r.label} className="flex items-center gap-3">
                    <span className="font-mono text-[9px] uppercase tracking-[0.28em] w-10" style={{ color: "var(--text-3)" }}>{r.label}</span>
                    <div className="flex-1 h-1 relative" style={{ background: "rgba(255,255,255,0.06)", borderRadius: 1 }}>
                      <div className="absolute top-0 left-0 h-full" style={{ width: `${r.pct}%`, background: r.pct >= 90 ? "#00B67A" : r.pct >= 5 ? "var(--gold)" : "var(--text-3)", borderRadius: 1 }} />
                    </div>
                    <span className="font-mono text-[9px] w-8 text-right" style={{ color: "var(--text-2)" }}>{r.pct}%</span>
                  </div>
                ))}
              </div>

              <a
                href={live ? `https://www.trustpilot.com/review/${DOMAIN}` : "#"}
                target={live ? "_blank" : undefined}
                rel={live ? "noopener noreferrer" : undefined}
                className="mt-8 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.34em] transition-colors"
                style={{ color: "var(--gold-2)" }}
                data-testid="trustpilot-read-all"
              >
                Read all reviews <ArrowUpRight size={12} />
              </a>

              <div className="mt-6 pt-5 flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.32em]" style={{
                borderTop: "1px solid var(--line)",
                color: "var(--text-3)",
              }}>
                <ShieldCheck size={12} style={{ color: "var(--gold)" }} />
                Verified · Independent · Public
              </div>
            </div>
          </motion.div>

          {/* Right — reviews */}
          <div className="lg:col-span-8">
            {live ? <LiveWidget /> : <PreviewCarousel />}
          </div>
        </div>
      </div>
    </section>
  );
}
